(ns ui.view.transaction-process.util
  (:require
   [clojure.string :as string]
   [common.currencies :as currencies]
   [common.eid :as eid]
   [common.entity :as entity]
   [common.localization :refer [tr]]
   [common.schema.registry :as registry]
   [common.transaction-process.access :as tp-access]
   [common.transaction-process.dictionary :as dict]
   [common.transaction-process.resolver :as resolver]
   [common.transaction-process.validation :as tp-validation]
   [common.util :as cu]
   [frontend.state.current-session :as session]
   [ui.components.input.percentage :as percentage-input]
   [ui.util :as uu]))

;; Schema option conversion

(defn schema-opts->input-opts
  "Convert schema option maps ({:key kw :label s}) to input option maps ({:value kw :label s})."
  [opts]
  (mapv (fn [{k :key :as m}] (-> m (dissoc :key) (assoc :value k))) opts))

;; Sub-entity resolution

(defn resolve-refs
  "Resolve a collection of refs ({:db/id n} or raw/legacy ids) to their session
   entity maps, dropping unresolvable and soft-deleted ones. The one imperative
   resolution recipe for ref-many fields (sub-entities, roles, conditions, …)."
  [refs]
  (->> refs
       (keep #(session/get-entity (eid/eid %)))
       (remove :common/disabled?)
       vec))

(defn get-sub-entities
  "Given a TP entity and a multi-ref field kw, resolves all sub-entity data."
  [entity-data field-kw]
  (resolve-refs (get entity-data field-kw)))

(defn resolve-slot-instance
  "Resolve a named single-ref slot (`slot-kw`) off the TP entity to its child
   entity map via the session, the way `get-sub-entities` resolves a ref-many.
   Returns nil when the slot is empty or the resolved entity is disabled."
  [entity-data slot-kw]
  (when-let [ref (get entity-data slot-kw)]
    (let [entity (session/get-entity (eid/eid ref))]
      (when-not (:common/disabled? entity)
        entity))))

(defn tp-with-resolved-slots
  "Build a copy of the TP entity where every top-level entity-slot key in the
   dictionary is replaced by its resolved child entity map (or nil). The resolver
   (`incomplete-required-slots`, `slots-by-category`) reads child maps straight off
   the slot attributes, so callers must resolve the refs through the session first."
  [dictionary entity-data]
  (reduce (fn [tp [slot-kw _]]
            (assoc tp slot-kw (resolve-slot-instance entity-data slot-kw)))
          entity-data
          (resolver/entity-slots dictionary)))

(defn attachment-slot-field?
  [field-config]
  (= :data.type/attachment-slot (:field/data-type field-config)))

(defn missing-mandatory-fields
  "Given the visible-fields output (each {:field-kw :field-config :mandatory?})
   and entity-data, returns the mandatory subset still missing. Attachment-slot
   fields share this one gate with scalar fields — a slot is 'present' when an
   attachment carries its slot id (= the field-kw) in `:common/system-kws`, so
   documents and scalar fields go through a single codepath. `attachments`
   is the resolved attachment seq (defaults to the entity's inline list)."
  ([visible-fields entity-data]
   (missing-mandatory-fields visible-fields entity-data (:common/attachments entity-data)))
  ([visible-fields entity-data attachments]
   (->> visible-fields
        (filter :mandatory?)
        (filter (fn [{:keys [field-kw field-config]}]
                  (if (attachment-slot-field? field-config)
                    (not (some #(contains? (set (:common/system-kws %)) field-kw) attachments))
                    (cu/blank-value? (get entity-data field-kw)))))
        (mapv (fn [{:keys [field-kw field-config]}]
                {:field-kw field-kw
                 :label    (:field/label field-config)})))))

(defn validate-transition-field-values
  "Given a transition's :transition/field-validations map and the user's entered
   field values, returns a map of field-kw -> error-descriptor for any violations."
  [field-validations field-vals]
  (tp-validation/validate-transition-field-values
   {:field-validations field-validations
    :field-vals        field-vals}))

(defn validation-error-message
  [error field-configs]
  (case (:rule error)
    :min-business-days-from-today
    (tr :transaction-process/min-business-days-error [(:n error)])

    :less-than-or-equal-to-field
    (tr :transaction-process/must-not-exceed-field
        [(get-in field-configs [(:comparison-field error) :field/label])])

    nil))

(defn missing-fields-tooltip
  "Builds hiccup for a tooltip listing missing mandatory fields. Shows the first
   5 by label, then a truncation line ('and N other mandatory fields')."
  [missing]
  (let [visible (take 5 missing)
        extra   (- (count missing) 5)]
    [:ul {:class "list-disc pl-4 m-0"}
     (concat
      (->> visible
           (map-indexed (fn [idx {:keys [field-kw label]}]
                          ^{:key (str "tp-missing-" (or field-kw idx))}
                          [:li label]))
           doall)
      (when (pos? extra)
        [^{:key "tp-missing-more"}
         [:li (tr :and-x-more-missing-fields [extra])]]))]))

(defn ready-errors-tooltip
  "Builds a save-ready tooltip from rule messages and missing-field errors."
  [errors]
  (let [messages       (keep :message errors)
        missing-fields (filterv :label errors)]
    [:div
     (doall
      (map-indexed
       (fn [idx message]
         ^{:key (str "tp-ready-message-" idx)}
         [:div message])
       messages))
     (when (seq missing-fields)
       [:div
        [:div {:class "mb-1"} (tr :transaction-process/save-ready-missing)]
        (missing-fields-tooltip missing-fields)])]))

(defn forward-blockers-tooltip
  "Tooltip listing missing mandatory fields, missing mandatory document slots,
  and incomplete required sub-entities. All three categories share a flat
  bullet list keyed by their :label; each category's labels are descriptive
  enough to read without subsection headers."
  [{:keys [missing-fields missing-slots blocking-sub-entities]}]
  (let [items (concat
               (mapv (fn [{:keys [label]}] {:label label :kind :field})
                     missing-fields)
               (mapv (fn [{:keys [label]}] {:label label :kind :slot})
                     missing-slots)
               (mapv (fn [{:keys [label category]}]
                       {:label label :kind category})
                     blocking-sub-entities))
        visible (take 5 items)
        extra   (- (count items) 5)]
    [:ul {:class "list-disc pl-4 m-0"}
     (concat
      (->> visible
           (map-indexed (fn [idx {:keys [label]}]
                          ^{:key (str "tp-block-" idx)}
                          [:li label]))
           doall)
      (when (pos? extra)
        [^{:key "tp-block-more"}
         [:li (tr :and-x-more-missing-fields [extra])]]))]))

(defn field-visible?
  "Check if a field should be visible using the survey rule language. value-lookup
   is the process (or sub-entity) map; rules read real attributes off it. An absent
  `:field/visibility` defaults to `:visibility/always`, matching the resolver."
  [{:keys [field-config value-lookup]}]
  (resolver/field-visible? field-config value-lookup))

;; Slot dictionary lookups

(defn dictionary-entry
  "Resolve a slot's dictionary entry by its slot-kw. Returns nil on miss."
  [template slot-kw]
  (get (:template/dictionary template) slot-kw))

;; Requiredness resolution

(defn resolve-required?
  "Resolve whether a sub-entity slot is required. Tri-state: true (Needed),
   false (Not needed) or nil (unselected — user hasn't decided). Delegates to the
   shared engine `resolver/slot-required?` over the slot's dictionary `entry`, so
   the cards' needed-selector and the gating engine share one implementation.
   Decisions resolve from the process's needed-field (read out of `lookup`);
   attestations/declarations from the static template default. `lookup` must carry
   the process entity-data + vehicle rule keys."
  [{:keys [entry lookup]}]
  (resolver/slot-required? entry lookup))

(defn requiredness-editable?
  "Whether a slot's Needed/Not-needed selector is user-editable on Reviewing. Only
   decisions carry an editable per-process needed-field; attestations and
   declarations have no per-process override (their requiredness is static)."
  [entry]
  (resolver/slot-editable? entry))

;; Error rendering — surfaces bugs instead of silently falling back

(defn log-resolve-failure!
  [{:keys [context value]}]
  (js/console.error (str "[transaction-process] " context)
                    (clj->js {:value value :type (type value)})))

(defn render-error
  "Log the failure and return a red hiccup span so the bug is obvious in the UI.
   Use at the call site when schema lookup or enum mapping returns nothing."
  [{:keys [context value] :as opts}]
  (log-resolve-failure! opts)
  [:span {:class "text-red-600 font-mono text-xs m-0 font-semibold"
          :title (str context " - value: " (pr-str value))}
   (str (tr :transaction-process/error-resolving-value) ": " (pr-str value))])

(defn error-text
  "Plain string variant of render-error for contexts that can't render hiccup (PDF)."
  [{:keys [value] :as opts}]
  (log-resolve-failure! opts)
  (str "⚠ " (tr :transaction-process/error-resolving-value) ": " (pr-str value)))

;; Shared enum label lookups — single source of truth for keyword → localized string

(defn lookup-keyword-label
  "Resolve a keyword value's display label from the field's options.
   Checks the template's :field/options first (when a template is provided),
   then falls back to the tenant schema's :options. Returns nil on miss —
   callers surface the failure explicitly."
  ([field-kw value]
   (lookup-keyword-label nil field-kw value))
  ([template field-kw value]
   (or (when-let [opts (get-in template [:template/fields field-kw :field/options])]
         (some #(when (= (:value %) value) (:label %)) opts))
       (when-let [opts (some-> @session/tenant-schema
                               (registry/get-field-schema field-kw)
                               :options)]
         (some #(when (= (:key %) value) (:label %)) opts)))))

(defn field-label
  "Resolve a field's display label. Checks the template's field-config first,
   falls back to :field/translations :en on the tenant schema. Returns nil on
   miss — callers surface the failure explicitly."
  [field-kw field-config]
  (or (:field/label field-config)
      (some-> @session/tenant-schema
              (registry/get-field-schema field-kw)
              :field/translations
              :en)))

(defn condition-attrs
  "The attribute keys for a condition entity, keyed off its `:entity/type`
   (attestation- vs decision-condition). One source for every read surface (peek
   panel, PDF) so the per-type keyword set isn't restated per view."
  [condition]
  (let [ns (if (= :entity.type/attestation-condition (:entity/type condition))
             "attestation-condition"
             "decision-condition")]
    {:title-kw       (keyword ns "title")
     :description-kw (keyword ns "description")
     :status-kw      (keyword ns "status")
     :status-true    (keyword (str ns ".status") "true")
     :status-false   (keyword (str ns ".status") "false")}))

(defn declaration-status-options
  "Single source of truth for the three selectable declaration statuses."
  []
  [{:value :declaration-status/not-applicable :label (tr :not-applicable)}
   {:value :declaration-status/pending        :label (tr :pending)}
   {:value :declaration-status/completed      :label (tr :completed)}])

(defn flags->declaration-status
  [{:keys [not-applicable? completed?]}]
  (cond
    not-applicable? :declaration-status/not-applicable
    completed?      :declaration-status/completed
    :else           :declaration-status/pending))

(defn declaration-status->flags
  [status]
  {:not-applicable? (= status :declaration-status/not-applicable)
   :completed?      (= status :declaration-status/completed)})

(defn decision-status
  [decision]
  (cond
    (nil? decision)
    {:state :pending :label (tr :pending) :variant :neutral}

    (:decision/draft? decision)
    {:state :draft :label (tr :draft) :variant :warning}

    :else
    {:state :logged :label (tr :logged) :variant :success}))

;; Status badge (shared across TP cards and detail panels)

(defn status-badge
  "Render a small status badge with consistent styling.
   Variants: :success, :warning, :danger, :neutral, :active"
  [{:keys [label variant class]}]
  (let [color (case variant
                :success "bg-primary-light text-primary-regular-text-accent"
                :warning "bg-yellow-100 text-yellow-700"
                :danger  "bg-red-100 text-red-700"
                :neutral "bg-gray-100 text-gray-600"
                :active  "bg-primary-regular text-primary-regular-text"
                "bg-gray-100 text-gray-600")]
    [:span {:class (uu/cm "px-2 py-0.5 rounded text-xs m-0 font-medium" color class)}
     label]))

;; Value formatting (shared between transaction-summary, details-modal, print-view)

(defn format-date
  "Format a date value for display. Handles epoch millis (long), date strings, and nil."
  [value]
  (cond
    (nil? value) nil
    (number? value) (session/long->tenant-date value)
    (string? value) value
    :else (str value)))

(defn materialize-field
  "Render a field value using the tenant schema-aware formatter.
   Handles currency symbols, dates, keyword option labels, and ref lookups."
  [{:keys [field entity id->entity show-currency?]}]
  (let [id->entity (or id->entity @session/entities)]
    (uu/field-value->materialized-value
     {:field          field
      :value          (get entity field)
      :entity         entity
      :tenant         @session/tenant
      :tenant-schema  @session/tenant-schema
      :id->entity     id->entity
      :show-currency? (if (some? show-currency?) show-currency? true)})))

(defn ref-multi-labels
  "Materialize a ref-multi field and extract the sorted list of labels."
  [{:keys [field entity id->entity]}]
  (->> (materialize-field {:field      field
                           :entity     entity
                           :id->entity id->entity})
       (keep :label)
       (remove string/blank?)
       seq))

(defn- materialized->display
  "Coerce a materialized field value to a display string. Most values come back
   as strings already; multi-valued fields come back as a seq (of labels or
   {:label ...} maps), which we join."
  [v]
  (cond
    (nil? v)        nil
    (string? v)     v
    (sequential? v) (->> v
                         (map #(if (map? %) (:label %) (str %)))
                         (remove string/blank?)
                         (string/join ", "))
    :else           (str v)))

(defn keyword-label
  "Look up the display label for a keyword value from field options.
   Returns nil on miss — callers decide how to surface the failure."
  [field-cfg kw]
  (->> (:field/options field-cfg)
       (some #(when (= (:value %) kw) (:label %)))))

(defn- ref-display-value
  [{:keys [field-kw data-type value]}]
  (let [refs (if (= :data.type/ref-multi data-type) value [value])]
    (->> refs
         (map (fn [ref]
                (if-let [resolved (if (map? ref) ref (session/get-entity ref))]
                  (entity/display-string resolved)
                  (error-text {:context (str "Ref not resolvable for " field-kw)
                               :value ref}))))
         (remove string/blank?)
         (string/join ", "))))

(defn attestation-outcome-options
  "The per-slot outcome option list, read off the attestation slot's dictionary
   `entry` (`:slot/template :attestation/outcome :field/options`)."
  [entry]
  (get-in entry [:slot/template :attestation/outcome :field/options]))

(defn attestation-outcome-option
  "Resolve the full option map ({:value :label :variant :requires-conditions?})
  for an outcome value off the slot `entry`'s per-slot outcome options. One source
  for the outcome's label, badge variant and conditions-trigger — none of which the
  frontend hardcodes. Returns nil on miss."
  [entry outcome]
  (cu/find-first-by :value outcome (attestation-outcome-options entry)))

(defn attestation-outcome-label
  "Display label for an attestation outcome. nil on miss — callers surface."
  [entry outcome]
  (:label (attestation-outcome-option entry outcome)))

(defn attestation-outcome-variant
  "Status-badge variant for an attestation outcome, read off the resolved option
  (`:variant`). Unknown/unmarked values fall through to `:neutral`."
  [entry outcome]
  (or (:variant (attestation-outcome-option entry outcome)) :neutral))

(defn attestation-requires-conditions?
  "Whether the given outcome opens the conditions editor — read off the option's
  `:requires-conditions?` marker."
  [entry outcome]
  (boolean (:requires-conditions? (attestation-outcome-option entry outcome))))

(defn all-conditions-met?
  "True when an attestation has at least one condition and every (resolved)
  condition is marked met. Resolves the condition refs against the live session
  (`resolve-refs`), so it works in both reactive views and the non-reactive PDF
  builder. Decisions/declarations don't reach here — only attestations carry a
  user-visible met/not-met status."
  [attestation]
  (let [conditions (resolve-refs (:attestation/conditions attestation))]
    (and (seq conditions)
         (every? (fn [c]
                   (let [{:keys [status-kw status-true]} (condition-attrs c)]
                     (= (get c status-kw) status-true)))
                 conditions))))

(defn attestation-display-label
  "Display label for an attestation's outcome, overridden to 'All conditions met'
  once the conditional outcome has every
  condition met. Falls back to the plain outcome label otherwise; nil only for an
  unknown outcome (callers surface that)."
  [entry attestation]
  (let [outcome (:attestation/outcome attestation)]
    (if (and (attestation-requires-conditions? entry outcome)
             (all-conditions-met? attestation))
      (tr :transaction-process/all-conditions-met)
      (attestation-outcome-label entry outcome))))

(defn reservation-layout
  "Resolved nested layout for the decision modal's Reservations section. Walks
  `:template/reservation-layout` (rows of field-kws and nested sub-group vectors)
  and replaces each field-kw with its enriched config from `:template/fields`
  (label, data-type, :field/grow, :field/min-width), preserving the nesting. The
  render layer turns this into nested flex-wrap rows."
  [template]
  (let [fields-config (:template/fields template)
        resolve-node  (fn resolve-node [node]
                        (if (vector? node)
                          (mapv resolve-node node)
                          (assoc (get fields-config node) :field/attribute node)))]
    (mapv resolve-node (:template/reservation-layout template))))

(defn reservation-display-fields
  "Flat, ordered list of reservation field nodes (each with :field/attribute,
  :field/label, :field/data-type) for read-only display (e.g. the decision peek).
  Derived from the same template layout the editing modal uses and filtered to the
  fields a reservation type enables, so the peek can't drift from the modal."
  [template enabled?]
  (->> (reservation-layout template)
       (tree-seq sequential? seq)
       (filter map?)
       (filterv #(enabled? (:field/attribute %)))))

(defn slot-visible?
  "Respect a slot's dictionary visibility rules (`:slot/visibility` +
  `:slot/visibility-rules`). Returns true when the slot should render for the given
  process (e.g. the Lux attestation only on Lux funds). Default — no visibility key
  — is true. `rule-lookup` is the pre-merged map of process attrs + vehicle rule
  keys produced by the caller's subscription."
  [rule-lookup entry]
  (resolver/slot-visible? entry rule-lookup))

(defn slot-in-scope?
  "Apply a slot's visibility and existing-instance retention policy."
  [{:keys [rule-lookup entry instance]}]
  (resolver/slot-in-scope? entry rule-lookup (some? instance)))

(defn format-field-value
  "Format a field value for display based on data type.
   Returns a string; on unresolvable values logs to console and returns a
   visible error string so the bug surfaces instead of being silently swallowed."
  [{:keys [field-kw field-cfg entity-data]}]
  (let [value     (get entity-data field-kw)
        data-type (:field/data-type field-cfg)]
    (cond
      (nil? value) nil

      (= data-type :data.type/currency-value)
      (let [sym-kw (cu/currency->symbol field-kw)
            sym (or (get entity-data sym-kw) @session/default-currency)
            num-val (if (uu/is-bigdec? value) (uu/bigdec->float value) value)
            formatted (uu/format-number-with-mult nil num-val nil)]
        (if sym
          (str (currencies/get-label sym) " " formatted)
          formatted))

      (= data-type :data.type/percentage)
      (percentage-input/format-value {} value)

      (= data-type :data.type/boolean)
      (if value (tr :yes) (tr :no))

      (= data-type :data.type/date)
      (format-date value)

      (= data-type :data.type/keyword-multi)
      (when (seq value)
        (->> value
             (map #(or (keyword-label field-cfg %)
                       (error-text {:context (str "No option label for " field-kw)
                                    :value %})))
             (string/join ", ")))

      (= data-type :data.type/keyword)
      (or (keyword-label field-cfg value)
          (error-text {:context (str "No option label for " field-kw)
                       :value value}))

      (or (:ref? field-cfg)
          (contains? #{:data.type/ref-single :data.type/ref-multi} data-type))
      (ref-display-value {:field-kw field-kw
                          :data-type data-type
                          :value value})

      (keyword? value)
      (error-text {:context (str "Unmapped keyword for " field-kw)
                   :value value})

      :else (str value))))

(defn summary-field-display
  "Render-ready {:field-kw :label :value} for one sidebar summary field. Prefers
   the per-customer template field-config (label + value formatting, incl. enum and
   Yes/No overrides) when the field has one — so the summary renders a field exactly
   as its card does — and falls back to the tenant-schema materializer for bare
   fields with no template config (vehicle, status, …). Nothing is hardcoded either
   way; the summary template carries only the field-kw."
  [{:keys [field-kw entity field-configs id->entity]}]
  (let [field-cfg    (get field-configs field-kw)
        schema-field (registry/get-field-schema @session/tenant-schema field-kw)
        schema-ref?  (contains? #{:data.type/ref-single :data.type/ref-multi}
                                (:field/data-type schema-field))]
    {:field-kw field-kw
     :label    (field-label field-kw field-cfg)
     :value    (if (and field-cfg (not schema-ref?))
                 (format-field-value {:field-kw field-kw :field-cfg field-cfg :entity-data entity})
                 (materialized->display
                  (materialize-field {:field      field-kw
                                      :entity     entity
                                      :id->entity id->entity})))}))

;; Slots-by-category: the named-ref lookup for a sub-entity category.
;; `resolver/slots-by-category` reads each slot's instance straight off the
;; resolved-slots TP map, so the cards iterate the layout and look up the
;; per-slot {:slot-kw :entry :instance} via these subs (see sub.cljs).

(defn slots-by-category
  "Resolve a category's top-level entity-slots to [{:slot-kw :entry :instance}].
   `dictionary` is `(:template/dictionary template)`; `entity-data` is the TP
   entity (slot refs are resolved here before delegating to the resolver)."
  [dictionary entity-data category]
  (resolver/slots-by-category dictionary
                              (tp-with-resolved-slots dictionary entity-data)
                              category))

(def ^:private category->card-type
  "Map of dictionary category → form card-type that owns its layout."
  {:decisions    :decisions
   :attestations :verifications
   :declarations :declarations})

(defn category-slot-order
  "The template-declared slot order for `category`, derived from the form
   layout — walks every stage's cards of the corresponding card-type and
   collects `:card/slots` (or `:card/groups[*].group/slots`) in declaration
   order, deduped across stages. Single source of truth so the read views
   (details modal, PDF) follow the same order as the form cards."
  [template category]
  (let [card-type (category->card-type category)]
    (->> (:template/stages template)
         (mapcat :stage/cards)
         (filter #(= card-type (:card/type %)))
         (mapcat (fn [c]
                   (or (:card/slots c)
                       (mapcat :group/slots (:card/groups c)))))
         distinct
         vec)))

(defn displayable-slots-by-category
  "Slots of `category` worth showing in a read view (details modal / PDF): the
   resolved [{:slot-kw :entry :instance}] kept to those that are **logged** (have an
   instance) OR **required AND visible**. This hides non-visible un-logged slots
   (e.g. a DD declaration excluded by `:dd-types-conducted`) and not-required
   un-logged ones (e.g. an optional attestation never started), while still surfacing
   outstanding required work as pending. `entity-data` is the TP entity, also used as
   the rule lookup for visibility/requiredness (same as the form cards).

   Slots are returned in the template-declared order via `category-slot-order`
   (single source of truth with the form cards). Any slot the template doesn't
   place in a card is appended at the end (in dictionary order) so it still surfaces."
  [template entity-data category]
  (let [matched (slots-by-category (:template/dictionary template) entity-data category)
        by-slot (->> matched (map (juxt :slot-kw identity)) (into {}))
        ordered (->> (category-slot-order template category)
                     (keep by-slot))
        ordered-kws (->> ordered (map :slot-kw) set)
        leftover (->> matched (remove #(contains? ordered-kws (:slot-kw %))))]
    (->> (concat ordered leftover)
         (filterv (fn [{:keys [entry instance]}]
                    (and (slot-in-scope? {:rule-lookup entity-data
                                          :entry       entry
                                          :instance    instance})
                         (or (some? instance)
                             (resolve-required? {:entry entry :lookup entity-data}))))))))

(defn template-value-fields
  "The value-field / attachment-slot keys of a (nested) dictionary template. Used
   by the ready-error gates and the read views to walk a sub-entity's own scalar
   fields."
  [template]
  (->> template
       (filter (fn [[_ e]] (contains? #{:field.kind/value :field.kind/attachment-slot}
                                      (dict/entry-kind e))))
       (mapv first)))

(def ^:private category-status-fields
  "Per-category fields the read views render as a status badge rather than a
   labelled value row, so they are dropped from `slot-display-fields`. Decisions
   carry their status in `:decision/draft?` (not a template field), so none here."
  {:attestations #{:attestation/outcome}
   :declarations #{:declaration/not-applicable? :declaration/completed?}})

(defn slot-display-fields
  "The read-display value-fields of a slot, in template order: the slot template's
   value-fields minus the category's status field(s) (rendered as a badge instead).
   All three read surfaces (peek panel, details modal, PDF) derive their labelled
   rows from this single source so they can't drift."
  [{:keys [slot-template category]}]
  (let [status-fields (get category-status-fields category #{})]
    (->> (template-value-fields slot-template)
         (remove status-fields)
         vec)))

(defn field-mandatory?
  "Resolve a template field-config's mandatoriness in an always-visible context
  (the decision-modal sub-entity fields are shown unconditionally; per-type field
   gating is handled by callers). value-lookup feeds conditional rules. nil config
   (field not in the template) resolves to optional."
  [field-config value-lookup]
  (resolver/field-mandatory? {:entry    field-config
                              :visible? true
                              :lookup   value-lookup}))

(defn fields-in-namespace
  "Template field-kws (carrying a user-facing :field/label) whose namespace equals
   ns-str. The label filter drops structural ref-list fields like
   :decision/participants that have no input label."
  [field-configs ns-str]
  (->> (keys field-configs)
       (filter #(= ns-str (namespace %)))
       (filter #(:field/label (get field-configs %)))
       vec))

(defn- group-missing-field-labels
  "Sorted distinct :field/labels of fields that are mandatory (per the template
   field-configs) and blank in at least one instance. field-enabled? gates a
   field for a given instance (reservations disable fields per type). `lookup-base`
   is the ancestor (process) map merged under each instance for rule evaluation, so
   a child field's conditional mandatoriness can reference a TP field; blank-checks
   still read the instance's own value (namespaces don't collide)."
  [{:keys [field-configs fields instances field-enabled? lookup-base]}]
  (let [field-enabled? (or field-enabled? (fn [_ _] true))]
    (->> fields
         (filter (fn [fk]
                   (some (fn [inst]
                           (and (field-enabled? inst fk)
                                (field-mandatory? (get field-configs fk) (merge lookup-base inst))
                                (cu/blank-value? (get inst fk))))
                         instances)))
         (keep (fn [fk] (:field/label (get field-configs fk))))
         distinct
         sort
         vec)))

(defn- reservation-field-enabled?
  "Reservations may disable individual form fields per type via
   `:investment-reservation-type/enabled-fields`; reservation-enabled-fields maps
   type-id -> set of enabled field-kws (nil = all enabled)."
  [reservation-enabled-fields reservation field-kw]
  (let [type-ref (:investment-reservation/type reservation)
        type-id  (or (:db/id type-ref) type-ref)
        enabled  (get reservation-enabled-fields type-id)]
    (or (nil? enabled) (contains? enabled field-kw))))

(defn decision-ready-errors
  "Missing-field descriptors blocking a decision from being saved as ready.
   Everything is derived from the template `field-configs` (mandatoriness AND
   labels), so a customer changing which fields are required needs no frontend
   change. The decision date is listed individually by its label; each sub-entity
   group (participants / conditions / reservations) collapses to one
   '<Group>: <fields>' line naming only the still-missing template fields, so
   several incomplete rows of the same kind don't spam the list. The sub-entity
   field sets are derived by field-kw namespace, never hardcoded.
   `reservation-enabled-fields` maps a reservation type-id -> set of enabled
   field-kws (nil = all enabled)."
  [{:keys [decision participants conditions reservations field-configs
           reservation-enabled-fields process]}]
  (let [;; Decision-level: scoped to the field(s) the Details section actually
        ;; renders (just the date) so the gate can't block on an unrendered field.
        decision-missing
        (group-missing-field-labels
         {:field-configs field-configs
          :fields        [:decision/date]
          :instances     [decision]
          :lookup-base   process})

        group-line
        (fn [label-key labels]
          (when (seq labels)
            {:label (str (tr label-key) ": " (string/join ", " labels))}))

        participant-line
        (group-line :participants
                    (group-missing-field-labels
                     {:field-configs field-configs
                      :fields        (fields-in-namespace field-configs "decision-participant")
                      :instances     participants
                      :lookup-base   process}))

        condition-line
        (group-line :conditions
                    (group-missing-field-labels
                     {:field-configs field-configs
                      :fields        (fields-in-namespace field-configs "decision-condition")
                      :instances     conditions
                      :lookup-base   process}))

        reservation-line
        (group-line :investment-reservations
                    (group-missing-field-labels
                     {:field-configs  field-configs
                      :fields         (fields-in-namespace field-configs "investment-reservation")
                      :instances      reservations
                      :lookup-base    process
                      :field-enabled? (fn [r fk]
                                        (reservation-field-enabled?
                                         reservation-enabled-fields r fk))}))]
    (cond-> []
      (seq decision-missing) (into (mapv (fn [label] {:label label}) decision-missing))
      participant-line       (conj participant-line)
      condition-line         (conj condition-line)
      reservation-line       (conj reservation-line))))

(defn attestation-ready-errors
  "Missing-field descriptors blocking an attestation from being saved as ready.
   Mandatoriness AND labels come from the template field-configs, walked over the
   slot template's value-fields (so the Lux slot — whose template omits
   `:attestation/date` — simply doesn't require a date). The modal asterisks and
   this gate therefore share one source. The conditions checks are structural to
   the 'subject to conditions' outcome. `values` is the form draft keyed by
   namespaced attrs; `slot-template` is the slot entry's `:slot/template`."
  [{:keys [field-configs slot-template values conditions process]}]
  (let [conditional?  (= (:attestation/outcome values)
                         :attestation.value/no-objections-subject-to-conditions)
        condition-ok? (fn [c] (and (cu/non-empty-string? (:title c)) (:status c)))]
    (cond-> (mapv (fn [label] {:label label})
                  (group-missing-field-labels
                   {:field-configs field-configs
                    :fields        (template-value-fields slot-template)
                    :instances     [values]
                    :lookup-base   process}))
      (and conditional? (empty? conditions))
      (conj {:label (tr :transaction-process/at-least-one-condition)})

      (and conditional? (seq conditions) (some (complement condition-ok?) conditions))
      (conj {:label (tr :transaction-process/condition-missing-fields)}))))

(defn declaration-ready-errors
  "Error descriptors blocking a declaration from being saved as ready.
   Field mandatoriness (e.g. `:declaration/date` once Completed) comes from the
   template field-configs over the slot template's value-fields — so the date
   asterisk and this gate flip on together. Pending is a selected status, but it
   cannot be saved as ready. `values` is the form draft keyed by namespaced attrs;
   `slot-template` is the slot entry's `:slot/template`."
  [{:keys [field-configs slot-template values process]}]
  (cond-> []
    (not (or (:declaration/not-applicable? values) (:declaration/completed? values)))
    (conj {:message (tr :transaction-process/pending-cannot-save-ready)})

    :always
    (into (mapv (fn [label] {:label label})
                (group-missing-field-labels
                 {:field-configs field-configs
                  :fields        (template-value-fields slot-template)
                  :instances     [values]
                  :lookup-base   process})))))

(defn get-team-assignments
  "Parse team-assignments-edn from entity data. Returns a map of role-id -> members."
  [entity-data]
  (tp-access/parse-team-assignments
   (:transaction-process/team-assignments-edn entity-data)))

(defn account-is-team-member?
  "Check if the given account-id is a member of any team in the assignments map."
  [team-assignments account-id]
  (tp-access/account-in-team-assignments? team-assignments account-id))
